import React from "react";
import { Empty, Table } from "antd";
import { BATCH_REPORT_TEST_STATUS } from "../../../../utils/statusConfig";
import {
  EMPTY_CELL_VALUE,
  OVERDUE_ELLIPSIS_COLUMN,
  formatOverdueCellValue,
  renderOverdueEllipsisCell,
  resolveTestCategoryName,
} from "../utils/overdueUtils";
import { resolveRetestCustomerCompanyName } from "../utils/retestUtils";
import {
  DATA_OPS_TABLE_PAGINATION,
  DATA_OPS_TABLE_SCROLL_Y,
  sumColumnWidths,
} from "../utils/utils";

const renderRetestStatusCell = (status) => {
  if (status == null || status === "") {
    return formatOverdueCellValue(status);
  }
  const statusObj = BATCH_REPORT_TEST_STATUS[String(status).trim()];
  if (!statusObj) {
    return formatOverdueCellValue(status);
  }
  return <span style={statusObj.style}>{statusObj.label}</span>;
};

/** Main list API columns — only fields returned by retest_traceability. */
const buildRetestListColumns = () => [
  {
    title: "Retest WO",
    dataIndex: "retest_workorder_id",
    key: "retest_workorder_id",
    width: 130,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
  {
    title: "GBID",
    dataIndex: "original_wo_gbid",
    key: "original_wo_gbid",
    width: 150,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
  {
    title: "Customer ID",
    dataIndex: "customer_id",
    key: "customer_id",
    width: 120,
    render: formatOverdueCellValue,
  },
  {
    title: "Sample Type",
    dataIndex: "sample_type",
    key: "sample_type",
    width: 120,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
  {
    title: "Test",
    dataIndex: "test_name",
    key: "test_name",
    width: 200,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
  {
    title: "Retest reason",
    dataIndex: "retest_reason",
    key: "retest_reason",
    width: 160,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
];

const resolveCustomerCompanyDisplay = (record, customerLookup) => (
  resolveRetestCustomerCompanyName(record, customerLookup) || EMPTY_CELL_VALUE
);

/**
 * Export / drill-down columns — same order and labels as Excel export.
 */
const buildRetestExportColumns = (categoryLabelById = null, customerLookup = null) => [
  {
    title: "Customer",
    key: "customer_company",
    width: 160,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: (_value, record) => (
      renderOverdueEllipsisCell(resolveCustomerCompanyDisplay(record, customerLookup))
    ),
  },
  {
    title: "Customer ID",
    dataIndex: "customer_id",
    key: "customer_id",
    width: 120,
    render: formatOverdueCellValue,
  },
  {
    title: "Retest WO",
    dataIndex: "retest_workorder_id",
    key: "retest_workorder_id",
    width: 130,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
  {
    title: "Original WO",
    dataIndex: "original_workorder_id",
    key: "original_workorder_id",
    width: 130,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
  {
    title: "GBID",
    dataIndex: "original_wo_gbid",
    key: "original_wo_gbid",
    width: 150,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
  {
    title: "Sample ID",
    dataIndex: "sample_id",
    key: "sample_id",
    width: 140,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
  {
    title: "Sample Type",
    dataIndex: "sample_type",
    key: "sample_type",
    width: 120,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
  {
    title: "Test Category",
    key: "test_category_name",
    width: 140,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: (_value, record) => {
      const categoryName = resolveTestCategoryName(
        record?.test_category_id,
        categoryLabelById,
      );
      return renderOverdueEllipsisCell(categoryName || EMPTY_CELL_VALUE);
    },
  },
  {
    title: "Test",
    dataIndex: "test_name",
    key: "test_name",
    width: 180,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
  {
    title: "Original Result",
    dataIndex: "original_result",
    key: "original_result",
    width: 130,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
  {
    title: "Retest Result",
    dataIndex: "rt_result",
    key: "rt_result",
    width: 130,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 100,
    render: renderRetestStatusCell,
  },
  {
    title: "Created By",
    dataIndex: "created_by_name",
    key: "created_by_name",
    width: 160,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
  {
    title: "Created At",
    dataIndex: "created_at",
    key: "created_at",
    width: 150,
    ellipsis: OVERDUE_ELLIPSIS_COLUMN,
    render: renderOverdueEllipsisCell,
  },
];

/**
 * Retest table — list columns for main tab; export columns for drill-down modal.
 */
export default function RetestTable({
  rows,
  loading,
  onViewRow,
  showExportColumns = false,
  categoryLabelById = null,
  customerLookup = null,
}) {
  const columns = showExportColumns
    ? buildRetestExportColumns(categoryLabelById, customerLookup)
    : buildRetestListColumns();
  const tableScrollX = sumColumnWidths(columns);

  return (
    <Table
      bordered
      className="DataOpsAnalytics__Table"
      size="middle"
      rowKey="rowKey"
      loading={loading}
      columns={columns}
      dataSource={rows}
      onRow={onViewRow
        ? (record) => ({
          onClick: () => onViewRow(record),
          className: "DataOpsAnalytics__ClickableRow",
        })
        : undefined}
      pagination={DATA_OPS_TABLE_PAGINATION}
      scroll={{ x: tableScrollX, y: DATA_OPS_TABLE_SCROLL_Y }}
      locale={{
        emptyText: (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
        ),
      }}
    />
  );
}
